/* eslint-disable max-len */

// Constants to replace magic numbers
const MAX_TEXT_LENGTH = 100;
const MAX_PARTICIPANTS = 2;

const initialState = [];

// Helper functions for better maintainability
const createTranscriptionEntry = (transcriptionData, additionalFields = {}) => ({
	peerConversation: transcriptionData?.peerConversation || '',
	peerId: transcriptionData?.peerId,
	displayName: transcriptionData?.peerName || transcriptionData?.displayName,
	isFinal: transcriptionData?.isFinal || false,
	...additionalFields
});

const limitParticipants = (state, maxCount = MAX_PARTICIPANTS) => 
	state.length > maxCount ? state.slice(0, maxCount) : state;

const removeParticipantById = (state, peerId) => {
	const index = state.findIndex(item => item?.peerId === peerId);
	return index > -1 
		? [...state.slice(0, index), ...state.slice(index + 1)]
		: state;
};

const addParticipantToFront = (state, participant) => 
	limitParticipants([participant, ...state]);

const updateExistingParticipant = (state, transcriptionData) => {
	const index = state.findIndex(item => item?.peerId === transcriptionData?.peerId);
	if (index === -1) return state;

	const existingParticipant = state[index];
	const stateWithoutCurrent = [...state.slice(0, index), ...state.slice(index + 1)];

	// Handle long text truncation
	if (existingParticipant?.peerConversation?.length > MAX_TEXT_LENGTH && !transcriptionData?.fromMobile) {
		const newParticipant = createTranscriptionEntry(transcriptionData);
		return limitParticipants([newParticipant, ...stateWithoutCurrent]);
	}

	// Handle mobile vs desktop updates
	const newParticipant = createTranscriptionEntry(transcriptionData);
	return limitParticipants([newParticipant, ...stateWithoutCurrent]);
};

const addNewParticipant = (state, transcriptionData) => {
	const newParticipant = createTranscriptionEntry(transcriptionData, {
		oldConversation: transcriptionData?.isFinal ? transcriptionData?.peerConversation : '',
		latest: transcriptionData?.peerConversation
	});
	
	return addParticipantToFront(state, newParticipant);
};

const validateTranscriptionData = (transcriptionData) => {
	if (!transcriptionData) {
		throw new Error('Transcription data is required');
	}
	
	const requiredFields = ['peerId', 'peerConversation'];
	const missingFields = requiredFields.filter(field => !transcriptionData[field]);
	
	if (missingFields.length > 0) {
		throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
	}
	
	return true;
};

const transcriptionreducer = (state = initialState, action) =>
{
	try {
		switch (action.type)
		{
			case 'SET_TRANSCRIPTION_DATA':
			{
				const { transcriptionData } = action.payload;
				
				// Validate input data
				try {
					validateTranscriptionData(transcriptionData);
				} catch (error) {
					console.error('Invalid transcription data:', error.message);
					return state; // Return unchanged state on validation error
				}

				const existingIndex = state.findIndex(item => item?.peerId === transcriptionData?.peerId);

				if (existingIndex > -1) {
					return updateExistingParticipant(state, transcriptionData);
				} else {
					return addNewParticipant(state, transcriptionData);
				}
			}

			case 'SET_OLD_TRANSCRIPTION_DATA':
			{
				const { transcriptionData } = action.payload;
				
				if (!transcriptionData?.peerId) {
					console.error('Missing peerId in old transcription data');
					return state;
				}

				const findIndex = state.findIndex(item => item?.peerId === transcriptionData?.peerId);

				if (findIndex > -1) {
					return [
						...state.slice(0, findIndex),
						transcriptionData,
						...state.slice(findIndex + 1)
					];
				}

				return state;
			}

			case 'CLOSE_PEER':
			{
				const { peerId } = action.payload;
				
				if (!peerId) {
					console.error('Missing peerId in close peer action');
					return state;
				}

				return removeParticipantById(state, peerId);
			}

			case 'CLEAR_CC_DATA':
			{
				return [];
			}

			default:
				return state;
		}
	} catch (error) {
		console.error('Transcription reducer error:', error);
		return state; // Always return valid state on error
	}
};

export default transcriptionreducer;
